import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { SectionTypes } from '@common/enums/section-type-enum';
import { ApplianceModel, ApplianceSource, ApplianceType } from '@common/models';
import { useFetchDependencies } from '@core/hooks/useFetchDependencies';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useFetchInStoreSinksQuery } from '@ee-api/sinks-api/inStoreSinksService';
import { useFetchCustomerSuppliedSinksQuery, useFetchProgramSinksQuery } from '@ee-api/sinks-api/sinksService';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useStores } from '@ee-api/stores-api/useStores';
import { selectInStoreSinksUrl, updateInStoreSinksUrl } from '@redux/slices/sinkSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { skipToken } from '@reduxjs/toolkit/query';

import { makeSinksSelector } from './sinks.utils';

/**
 * Defining empty array outside of selector as indicated in docs.
 * @see {@link https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results}
 */
const emptyArray = [] satisfies ApplianceModel[];

interface useSinkReturn {
    browsedSinks: ApplianceModel[];
    customerSuppliedSinks: ApplianceModel[];
    isFetchingSinks: boolean;
    isErrorSinks: boolean; // TODO: Refactor this. Not being used.
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const useSink = (): useSinkReturn => {
    const zipcode = useAppSelector(selectZipcode);
    const { stores } = useStores();
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();

    const dispatch = useAppDispatch();
    const inStoreSinksUrl = useAppSelector(selectInStoreSinksUrl);

    const { isMaterialLoaded } = useMaterials();

    const selectSinksTransformed = useMemo(() => makeSinksSelector(), []);
    const selectInStoreSinksTransformed = useMemo(() => makeSinksSelector(), []);
    const selectSinksCustomerSuppliedTransformed = useMemo(() => makeSinksSelector(), []);
    const { retailerSettings, getInStoreApplianceUrl } = useRetailerSettings();

    const { isFetchingDependencies } = useFetchDependencies();

    useEffect(() => {
        dispatch(updateInStoreSinksUrl(getInStoreApplianceUrl(SectionTypes.sink)));
    }, [dispatch, getInStoreApplianceUrl]);

    const {
        inStoreSinks,
        isFetching: isFetchingInStoreSinks,
        isError: isErrorInStoreSinks,
    } = useFetchInStoreSinksQuery(
        isFetchingDependencies || !inStoreSinksUrl || !zipcode || !selectedColor || !stores.length || !isMaterialLoaded
            ? skipToken
            : { url: inStoreSinksUrl },
        {
            selectFromResult: ({ data, ...rest }) => ({
                inStoreSinks: selectInStoreSinksTransformed(
                    data ?? emptyArray,
                    ApplianceSource.IN_STORE,
                    undefined,
                    true
                ),
                ...rest,
            }),
        }
    );

    const {
        customerSuppliedSinks,
        isFetching: isFetchingCustomerSuppliedSinks,
        isError: isErrorCustomerSuppliedSinks,
    } = useFetchCustomerSuppliedSinksQuery(
        isFetchingDependencies || !zipcode || !selectedColor || !stores.length || !selectedColor.qteGrpID
            ? skipToken
            : {
                  params: {
                      zipCode: zipcode,
                      storeId: (retailerSettings?.showStores ? selectedStore?.companyId : null) ?? undefined,
                      ...(selectedColor.qteGrpID ? { prodQteGrpID: selectedColor.qteGrpID } : {}),
                  },
              },
        {
            selectFromResult: ({ data, ...rest }) => {
                return {
                    customerSuppliedSinks: selectSinksCustomerSuppliedTransformed(
                        data ?? emptyArray,
                        ApplianceSource.PROGRAM,
                        ApplianceType.SINK_LABOR
                    ),
                    ...rest,
                };
            },
        }
    );

    const {
        programSinks,
        isFetching: isFetchingSinks,
        isError: isErrorSinks,
    } = useFetchProgramSinksQuery(
        isFetchingDependencies || !zipcode || !selectedColor || !stores.length || !selectedColor.qteGrpID
            ? skipToken
            : {
                  params: {
                      zipCode: zipcode,
                      storeId: (retailerSettings?.showStores ? selectedStore?.companyId : null) ?? undefined,
                      ...(selectedColor.qteGrpID ? { prodQteGrpID: selectedColor.qteGrpID } : {}),
                  },
              },
        {
            selectFromResult: ({ data, ...rest }) => {
                return {
                    programSinks: rest.isError
                        ? emptyArray
                        : selectSinksTransformed(data ?? emptyArray, ApplianceSource.PROGRAM, ApplianceType.SINK, true),
                    ...rest,
                };
            },
        }
    );

    const browsedSinks: ApplianceModel[] = useMemo(
        () => [...programSinks, ...inStoreSinks],
        [programSinks, inStoreSinks]
    );

    return {
        browsedSinks,
        customerSuppliedSinks,
        isFetchingSinks: isFetchingInStoreSinks || isFetchingSinks || isFetchingCustomerSuppliedSinks,
        isErrorSinks: isErrorInStoreSinks || isErrorSinks || isErrorCustomerSuppliedSinks,
    };
};

export default useSink;
